import { Badge, Card, Image, Tooltip, Typography } from 'antd';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BLUE3, ORANGE1, PURPLE1, PURPLE2, RED1 } from '../../../constants/color';
import { SHIPPING_METHOD } from '../../../constants/generalConstants';
import { FALLBACK_IMG } from '../../../constants/styles';
import { DEFAULT_FONT_SIZE } from '../../../constants/systemConstants';
import { GoodData } from '../../../types';

const IMAGE_HEIGHT = 100;
const NAME_ROWS = 2;

type GoodCardProps = {
  good: GoodData;
  /** Dims the card and blocks `onClick` */
  disabled?: boolean;
  /** Makes the whole card actionable (POS: tap to add) */
  onClick?: () => void;
  /** Action rendered under the price (select gallery: select/deselect button) */
  footer?: ReactNode;
  /** Click the image to open the large picture */
  imagePreview?: boolean;
  /** Fixed card height, to keep rows aligned */
  height?: number;
};

/**
 * Shared product card: picture, name, price/unit and the status ribbons
 * (weighted / min-order rule / deleted / SKU).
 *
 * Presentational only — the caller decides what a click does: pass `onClick` to
 * make the whole card actionable, or `footer` to render an action button under
 * the price.
 */
const GoodCard = ({
  good,
  disabled = false,
  onClick,
  footer,
  imagePreview = false,
  height,
}: GoodCardProps) => {
  const { t } = useTranslation();

  // Stack the floating ribbons so they don't overlap each other
  const badgeTop = (type: string) => {
    let top = 10;
    const gap = 30;
    const position: { [key: string]: boolean } = {};
    if (good.isDelete) position.isDelete = true;
    if (good.hasGoodsSkuAttr) position.hasGoodsSkuAttr = true;
    if (good.isWeightGoods) position.isWeightGoods = true;
    if (good.goodsNumberRule) position.goodsNumberRule = true;
    if (good.shippingMethod === SHIPPING_METHOD.PICKUP)
      position.isPickup = true;
    Object.keys(position).forEach((key, index) => {
      if (key === type) top += index * gap;
    });
    return top;
  };

  return (
    <>
      {good.isWeightGoods && (
        <Badge.Ribbon
          style={{
            position: 'absolute',
            top: badgeTop('isWeightGoods'),
            zIndex: 5,
          }}
          color={BLUE3}
          text={t('goods.goodsListColumns.isWeighted')}
        />
      )}
      {good.goodsNumberRule && (
        <Badge.Ribbon
          style={{
            position: 'absolute',
            top: badgeTop('goodsNumberRule'),
            zIndex: 5,
          }}
          color={ORANGE1}
          text={`${good.goodsNumberRule.description}${good.goodsNumberRule.number}`}
        />
      )}
      {good.shippingMethod === SHIPPING_METHOD.PICKUP && (
        <Badge.Ribbon
          style={{
            position: 'absolute',
            top: badgeTop('isPickup'),
            zIndex: 5,
          }}
          color={PURPLE1}
          text={t('goods.add/editGood.shippingMethod.PICKUP')}
        />
      )}
      <Badge.Ribbon
        color={good.isDelete ? RED1 : PURPLE2}
        style={{
          display:
            good.isDelete || good.hasGoodsSkuAttr ? undefined : 'none',
        }}
        text={
          good.isDelete
            ? t('goods.add/editGood.isDelete')
            : t('goods.goodsListColumns.sku')
        }
      >
        <Card
          hoverable={!!onClick && !disabled}
          onClick={!disabled && onClick ? onClick : undefined}
          style={{ height, opacity: disabled ? 0.5 : 1 }}
          bodyStyle={{ padding: 8 }}
          cover={
            <div
              style={{ display: 'flex', justifyContent: 'center', padding: 8 }}
            >
              <Image
                src={good.thumbPicPath || good.thumbPic}
                preview={
                  imagePreview
                    ? { src: good.largePicPath || good.largePic }
                    : false
                }
                fallback={FALLBACK_IMG}
                width="auto"
                height={IMAGE_HEIGHT}
              />
            </div>
          }
        >
          <Typography.Paragraph
            ellipsis={{ rows: NAME_ROWS, tooltip: good.goodsName }}
            style={{
              marginBottom: 4,
              fontSize: DEFAULT_FONT_SIZE,
              textAlign: 'center',
              // Reserve both rows so cards line up whether the name wraps or not
              minHeight: 44,
            }}
          >
            {good.goodsName}
          </Typography.Paragraph>
          {/* Keep to one line: goodsUnit can be arbitrarily long */}
          <Tooltip title={`$${good.shopPrice} ${good.goodsUnit}`}>
            <div
              style={{
                textAlign: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              <Typography.Text type="danger" strong>
                {`$${good.shopPrice} `}
              </Typography.Text>
              <Typography.Text type="secondary">
                {good.goodsUnit}
              </Typography.Text>
            </div>
          </Tooltip>
          <div style={{ textAlign: 'center' }}>
            <Typography.Text
              type={good.goodsNumber > 0 ? 'secondary' : 'danger'}
              style={{ fontSize: DEFAULT_FONT_SIZE }}
            >
              {`${t('goods.goodsListColumns.goodsNumber')}: ${
                good.goodsNumber ?? 0
              }`}
            </Typography.Text>
          </div>
          {footer && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: 10,
              }}
            >
              {footer}
            </div>
          )}
        </Card>
      </Badge.Ribbon>
    </>
  );
};

export default GoodCard;
